import { forwardRef, useEffect, useState } from 'react'
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    FloatingPortal,
    FloatingFocusManager,
} from '@floating-ui/react'
import styles from './ScoopMenu.module.scss'

/**
 * ScoopMenu - Unified menu component (inline list or popover menu)
 *
 * @param {Object} props - Component props
 * @param {HTMLElement} [props.anchorEl] - Anchor element for popover mode (if provided, renders as popover)
 * @param {boolean} [props.open=false] - Whether popover is open (only for popover mode)
 * @param {Function} [props.onClose] - Close handler (only for popover mode)
 * @param {Object} [props.anchorOrigin] - Anchor positioning {vertical, horizontal}
 * @param {Object} [props.transformOrigin] - Transform origin {vertical, horizontal}
 * @param {number} [props.offset=8] - Distance in pixels between menu and anchor element
 * @param {boolean} [props.hasBackdrop=false] - Whether to show backdrop overlay
 * @param {boolean} [props.disableClick=false] - Disable useClick interaction (for context menus controlled externally)
 * @param {boolean} [props.disableFocusManager=false] - Disable FloatingFocusManager (required for virtual elements)
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.style] - Inline styles for override
 * @param {React.ReactNode} props.children - Menu content
 * @param {React.Ref} ref - Forwarded ref to menu container
 */
export const ScoopMenu = forwardRef(
    (
        {
            anchorEl,
            open = false,
            onClose,
            anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
            transformOrigin = { vertical: 'top', horizontal: 'left' },
            offset: offsetValue = 8,
            hasBackdrop = false,
            disableClick = false,
            disableFocusManager = false,
            className = '',
            style,
            children,
            ...props
        },
        ref
    ) => {
        // Convert MUI-style origins to Floating UI placement
        const getPlacement = () => {
            const vAnchor = anchorOrigin.vertical
            const hAnchor = anchorOrigin.horizontal

            // Bottom placements (most common)
            if (vAnchor === 'bottom' && hAnchor === 'center') return 'bottom'
            if (vAnchor === 'bottom' && hAnchor === 'left') return 'bottom-start'
            if (vAnchor === 'bottom' && hAnchor === 'right') return 'bottom-end'

            // Top placements
            if (vAnchor === 'top' && hAnchor === 'center') return 'top'
            if (vAnchor === 'top' && hAnchor === 'left') return 'top-start'
            if (vAnchor === 'top' && hAnchor === 'right') return 'top-end'

            // Right placements (when center vertical or no vertical specified)
            if (vAnchor === 'center' && hAnchor === 'right') return 'right'

            // Left placements (when center vertical or no vertical specified)
            if (vAnchor === 'center' && hAnchor === 'left') return 'left'

            return 'bottom'
        }

        const { refs, floatingStyles, context } = useFloating({
            open: open && !!anchorEl,
            onOpenChange: (isOpen) => {
                if (!isOpen) onClose?.()
            },
            placement: getPlacement(),
            middleware: [offset(offsetValue), flip(), shift({ padding: 8 })],
            whileElementsMounted: autoUpdate,
            elements: {
                reference: anchorEl,
            },
        })

        const click = useClick(context, { enabled: !disableClick })
        const dismiss = useDismiss(context)
        const role = useRole(context, { role: 'menu' })

        const { getFloatingProps } = useInteractions([click, dismiss, role])

        // Track if the menu has been positioned to avoid flash/jump
        const [isReady, setIsReady] = useState(false)

        // Set ready state after positioning is complete
        useEffect(() => {
            if (open && anchorEl && floatingStyles.position) {
                // Small delay to ensure Floating UI has calculated position
                const timer = requestAnimationFrame(() => {
                    setIsReady(true)
                })
                return () => cancelAnimationFrame(timer)
            } else {
                setIsReady(false)
            }
        }, [open, anchorEl, floatingStyles.position])

        const menuClassName = [styles.menu, className].filter(Boolean).join(' ')

        // Determine mode: if 'open' prop is explicitly passed, use popover mode
        const isPopoverMode = open !== undefined

        // Inline mode (no popover behavior)
        if (!isPopoverMode) {
            return (
                <div ref={ref} className={menuClassName} role="menu" {...props} style={style}>
                    {children}
                </div>
            )
        }

        // Popover mode - don't render if closed or no anchor
        if (!open || !anchorEl) return null

        // Extract width/minWidth/maxWidth from style to apply to wrapper for proper sizing
        const { width, minWidth, maxWidth, ...restStyle } = style || {}

        const menuContent = (
            <div
                ref={refs.setFloating}
                className={styles.floatingMenuWrapper}
                style={{
                    ...floatingStyles,
                    width: width || undefined,
                    minWidth: minWidth || undefined,
                    maxWidth: maxWidth || undefined,
                    opacity: isReady ? 1 : 0,
                    transition: isReady ? 'opacity 0.15s ease-out' : 'none',
                }}
            >
                <div className={menuClassName} style={restStyle} {...getFloatingProps(props)}>
                    {children}
                </div>
            </div>
        )

        const wrappedContent = disableFocusManager ? (
            menuContent
        ) : (
            <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
                {menuContent}
            </FloatingFocusManager>
        )

        return (
            <FloatingPortal key="scoop-menu-portal">
                {hasBackdrop && <div className={styles.backdrop} onClick={onClose} />}
                {wrappedContent}
            </FloatingPortal>
        )
    }
)

// This is useful for debugging and React DevTools
ScoopMenu.displayName = 'ScoopMenu'
